/*
 * Copyright (c) 2022. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import abstractContractClaimTranslations from "./abstractContractClaimTranslations";

const purchaseContractTranslations = {
  label: "Kaufvertrag",
  buyerOrSeller: {
    question: "Sind Sie der Käufer oder Verkäufer?",
    label: "Käufer oder Verkäufer",
    options: {
      buyer: { label: "Käufer" },
      seller: { label: "Verkäufer" },
    },
  },
  channel: {
    question: (caseData: Record<string, any>) => (caseData.buyerOrSeller === "buyer" ? "Wo haben Sie die Ware gekauft?" : "Wo haben Sie die Ware verkauft?"),
    label: (caseData: Record<string, any>) => (caseData.buyerOrSeller === "buyer" ? "Wo gekauft?" : "Wo verkauft?"),
    options: {
      amazon: { label: "Amazon" },
      ebay: { label: "Ebay" },
      kleinanzeigen: { label: "Internetportal “Kleinanzeigen”" },
      onlineOther: { label: "Anderer Onlineshop" },
      offline: { label: "Stationärer Handel/Laden vor Ort" },
      fleaMarket: { label: "Flohmarkt" },
      vinted: { label: "Vinted" },
      other: { label: "Sonstiges" },
    },
  },
  contractNumber: {
    question: (caseData: Record<string, any>) => (caseData.channel === "kleinanzeigen" ? "Wie lautet Ihre Anzeigennummer?" : "Wie lautet Ihre Bestellnummer?"),
    label: (caseData: Record<string, any>) => {
      switch (caseData.channel) {
        case "amazon": {
          return "Amazon-Bestellnummer";
        }
        case "ebay": {
          return "Ebay-Bestellnummer";
        }
        case "kleinanzeigen": {
          return "Anzeigennummer";
        }
        case "onlineOther": {
          return "Bestellnummer";
        }
        case "offline": {
          return "Bestellnummer";
        }
        case "fleaMarket": {
          return "";
        }
        case "vinted": {
          return "Vinted-Bestellnummer";
        }
        case "other": {
          return "Bestellnummer";
        }
      }
    },
  },
  itemTitle: {
    question: (caseData: Record<string, any>) => (caseData.buyerOrSeller === "buyer" ? "Welche Ware haben Sie gekauft?" : "Welche Ware haben Sie verkauft?"),
    label: "Name der Ware/ Artikelbezeichnung",
  },
  articleNumber: {
    question: "Wie lautet die Artikelnummer (falls vorhanden)?",
    label: "Artikelnummer",
  },
  purchasePrice: {
    question: "Was war der genaue Kaufpreis (inkl. MwSt.)?",
    label: "Kaufpreis (inkl. MwSt.)",
  },
  goodsOrServiceProvidedStatus: {
    label: (caseData: Record<string, any>) => (caseData.buyerOrSeller === "buyer" ? "Ware vollständig erhalten?" : "Ware vollständig versendet?"),
    question: (caseData: Record<string, any>) =>
      caseData.buyerOrSeller === "buyer" ? "Haben Sie die Ware (vollständig) erhalten?" : "Haben Sie die Ware (vollständig) versendet?",
    options: {
      fully: {
        label: (caseData: Record<string, any>) =>
          caseData.buyerOrSeller === "buyer" ? "Ich habe die Ware vollständig erhalten." : "Ich habe die Ware vollständig versendet.",
      },
      partially: {
        label: (caseData: Record<string, any>) =>
          caseData.buyerOrSeller === "buyer" ? "Ich habe etwas erhalten, aber nicht vollständig." : "Ich habe etwas versendet, aber nicht vollständig.",
      },
      none: {
        label: (caseData: Record<string, any>) =>
          caseData.buyerOrSeller === "buyer" ? "Ich habe die Ware gar nicht erhalten." : "Ich habe die Ware gar nicht versendet.",
      },
    },
  },
  locationOfGood: {
    label: "Standort der Ware",
    question: "Wo befindet sich die Ware jetzt?",
    options: {
      buyer: { label: (caseData: Record<string, any>) => (caseData.buyerOrSeller === "buyer" ? "Bei mir" : "Beim Käufer") },
      seller: { label: (caseData: Record<string, any>) => (caseData.buyerOrSeller === "buyer" ? "Beim Verkäufer" : "Bei mir") },
      elsewhere: { label: "An einem anderen Ort" },
      unknown: { label: "Ich weiss es nicht" },
    },
  },
  hasDefects: {
    label: "Mängel?",
    question: (caseData: Record<string, any>) =>
      caseData.buyerOrSeller === "buyer"
        ? "Hat die Ware aus Ihrer Sicht Mängel bzw. ist sie beschädigt?"
        : "Behauptet der Käufer, dass die Ware mangelhaft bzw. beschädigt ist?",
    options: {
      yes: { label: "Ja" },
      no: { label: "Nein" },
    },
  },
  defectsDescriptionClient: {
    label: "Beschreibung Mängel",
    question: (caseData: Record<string, any>) =>
      caseData.buyerOrSeller === "buyer"
        ? "Bitte beschreiben Sie hier die Mängel so genau wie möglich"
        : "Bitte beschreiben Sie hier, was der Käufer an der Ware bemängelt",
  },
  numberRepairAttempts: {
    label: "Ausbesserungsversuche?",
    question: (caseData: Record<string, any>) =>
      caseData.buyerOrSeller === "buyer"
        ? "Wurde vom Verkäufer bereits versucht, die Mängel zu beheben und falls ja, wie oft?"
        : "Haben Sie bereits versucht die Mängel zu beheben und falls ja, wie oft?",

    options: {
      0: { label: "Nein" },
      1: { label: "Ja, einmal" },
      2: { label: "Ja, zweimal" },
      3: { label: "Ja, dreimal oder häufiger" },
    },
  },
  numberReplacementAttempts: {
    label: "Neulieferungsversuche?",
    question: (caseData: Record<string, any>) =>
      caseData.buyerOrSeller === "buyer"
        ? "Wurde vom Verkäufer bereits versucht, die Ware neu zu liefern und falls ja, wie oft?"
        : "Haben Sie bereits versucht, die Ware neu zu liefern und falls ja, wie oft?",

    options: {
      0: { label: "Nein" },
      1: { label: "Ja, einmal" },
      2: { label: "Ja, zweimal" },
      3: { label: "Ja, dreimal oder häufiger" },
    },
  },
  ...abstractContractClaimTranslations,
};

export default purchaseContractTranslations;
